import { Box, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { RouletteWinnerBox } from "./RouletteWinnerBox";
import type { Playlist } from "src/@types/api-types";
import { useSelector } from "react-redux";
import { colorsSelector } from "@redux/colorPalette/selectors";
import { withBamLayoutSelector } from "@redux/user/selectors";

type PartialSong = {
	title: string;
	artist: { name: string };
};

export const VerticalWheel = ({
	song,
	playlist,
}: {
	song: PartialSong;
	playlist: Playlist;
}) => {
	const colors = useSelector(colorsSelector);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [containerWidth, setContainerWidth] = useState<number>(1);
	const withBamLayout = useSelector(withBamLayoutSelector);

	const animationDuration = 10000; // 10s
	const itemHeight = containerWidth / 4;
	const visibleHeight = itemHeight * 2;

	const [isWinnerView, setIsWinnerView] = useState(false);
	const [offsetY, setOffsetY] = useState(0);
	const animationRef = useRef<number | null>(null);

	const titles = useMemo(
		() =>
			playlist.playlistSongs.map((el) => ({
				title: el.song.title,
				artist: el.song.artist.name,
			})),
		[playlist],
	);

	const extendedTitles = useMemo(() => {
		const averagePixelsPerSecond = 100;
		const itemCountNeeded = Math.ceil(
			((animationDuration / 1000) * averagePixelsPerSecond) / itemHeight,
		);

		const timesToRepeat = Math.ceil(itemCountNeeded / titles.length) + 1;

		return Array.from({ length: timesToRepeat }, () => titles).flat();
	}, [titles, animationDuration, itemHeight]);

	if (containerWidth === 0) {
		return null;
	}

	useEffect(() => {
		const winnerIndex = extendedTitles
			.map((s) => s.title)
			.lastIndexOf(song.title);

		const targetOffset =
			winnerIndex * itemHeight - (visibleHeight / 2 - itemHeight / 2);

		setOffsetY(0);
		setIsWinnerView(false);

		const start = performance.now();

		const animate = (now: number) => {
			const elapsed = now - start;
			const progress = Math.min(elapsed / animationDuration, 1);

			const eased = progress ** 0.25;
			const newOffset = targetOffset * eased;

			setOffsetY(newOffset);

			if (progress < 1) {
				animationRef.current = requestAnimationFrame(animate);
			} else {
				setIsWinnerView(true);
				animationRef.current = null;
			}
		};

		animationRef.current = requestAnimationFrame(animate);

		return () => {
			if (animationRef.current) cancelAnimationFrame(animationRef.current);
		};
	}, [song, itemHeight, visibleHeight, extendedTitles, animationDuration]);

	useEffect(() => {
		const updateWidth = () => {
			if (containerRef.current) {
				setContainerWidth(containerRef.current.offsetWidth);
			}
		};

		updateWidth();
		window.addEventListener("resize", updateWidth);
		return () => window.removeEventListener("resize", updateWidth);
	}, []);

	return (
		<RouletteWinnerBox display="block">
			<Box
				ref={containerRef}
				sx={{
					height: visibleHeight,
					overflow: "hidden",
					position: "relative",
					width: "100%",
					backgroundColor: withBamLayout ? colors.gamesHighlightColor : "#7622B2",
				}}
			>
				<Box
					sx={{
						transform: `translateY(-${offsetY}px)`,
						transition: isWinnerView ? "transform 0.3s ease-out" : "none",
					}}
				>
					{extendedTitles.map((item, i) => {
						const isWinner = isWinnerView && item.title === song.title;
						return (
							<Box
								key={`${item.title}-${i}`}
								sx={{
									height: itemHeight,
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									textAlign: "center",
									backgroundColor: "transparent",
									transform: isWinner ? "scale(1.1)" : "scale(1)",
									transition: "all 0.3s ease",
									fontFamily: "Roboto",
								}}
							>
								<Typography variant="body1" fontSize={containerWidth / 20}>
									{item.title}
								</Typography>
								<Typography variant="caption" fontSize={containerWidth / 30}>
									{item.artist}
								</Typography>
							</Box>
						);
					})}
				</Box>
			</Box>
		</RouletteWinnerBox>
	);
};
